import { Button } from '@daily/shared/components/Button';
import { CheckIcon } from '@daily/shared/components/Icons/CheckIcon';
import { Text } from '@daily/shared/components/Text';
import { useTheme } from '@daily/shared/contexts/Theme';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useDisplayName } from '/hooks/useDisplayName';
import { isSafari } from '/lib/browserConfig';

import {
  useIsSelected,
  useSelectParticipant,
} from './hooks/useSelectParticipant';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  participant: string;
  disabled: boolean;
}

export const Participant: React.FC<React.PropsWithChildren<Props>> = ({
  disabled,
  participant,
}) => {
  const displayName = useDisplayName(participant, true);
  const { deselectParticipant, selectedParticipants, selectParticipant } =
    useSelectParticipant();
  const isSelected = useIsSelected(participant);
  const { t } = useTranslation();

  const { fontFamilies } = useTheme();

  const imageRef = useRef<HTMLImageElement>(null);
  const multipleDragElement = document.getElementById('drag-image');

  useEffect(() => {
    // setDragImage in Safari is pretty buggy. In Chrome and Firefox, we can use
    // a regular div element as a ghost image when multiple participants are selected.
    // In Safari, we need to do some canvas tricks.
    if (!isSafari() && selectedParticipants.length < 2) return;
    // create canvas
    const canvas = document.createElement('canvas');
    canvas.width = 100;
    canvas.height = 100;

    // make canvas look acceptable
    const canvasContext = canvas.getContext('2d');
    canvasContext.fillRect(0, 0, 0, 0);
    canvasContext.fillStyle = 'white';
    canvasContext.font = `42px ${fontFamilies.regular}`;
    canvasContext.fillText('👥', 20, 60);

    // make canvas into an image that is accepted by setDragImage
    const image = new Image();
    image.src = canvas.toDataURL();
    imageRef.current = image;
  }, [fontFamilies.regular, selectedParticipants.length]);

  const handleDrag = (event) => {
    // when multiple participants are selected, drag them all
    if (selectedParticipants.length > 1) {
      if (isSafari()) {
        event.dataTransfer.setDragImage(imageRef.current, 0, 0);
      } else {
        event.dataTransfer.setDragImage(
          multipleDragElement,
          multipleDragElement.getBoundingClientRect().x,
          multipleDragElement.getBoundingClientRect().y
        );
      }
      return;
    }
    // only one participant is selected: no need to set a drag image
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', participant);
  };

  const onClick = () => {
    isSelected
      ? deselectParticipant(participant)
      : selectParticipant(participant);
  };

  return (
    <span className="participant">
      <Button
        id={`breakouts-participant-${participant}`}
        data-session-id={participant}
        draggable
        iconBefore={isSelected ? <CheckIcon size={14} /> : undefined}
        onClick={onClick}
        onDragStart={handleDrag}
        type="button"
        title={
          disabled
            ? t('breakouts.participantDisabled', {
                name: displayName,
              })
            : displayName
        }
        disabled={disabled}
        variant={isSelected ? 'primary' : 'secondary'}
      >
        <Text El="span" color="inherit" truncate>
          {displayName}
        </Text>
      </Button>
      <style jsx>{`
        .participant {
          display: inline-block;
          max-width: 100%;
        }
        .participant :global(.button) {
          border-radius: 4px;
          border: 1px solid transparent;
          font-family: ${fontFamilies.regular};
          max-width: 100%;
          padding: 8px 12px;
          pointer-events: auto; // show title on hover when participant is disabled
        }
        .participant :global(.button[disabled]) {
          cursor: not-allowed;
          pointer-events: none;
        }
      `}</style>
    </span>
  );
};
